// vault-lib.js (Password Key version with Argon2id + HMAC Signing + Session Auto-Timeout + Multi Vault Support with Individual Timeouts, Auto-Refresh, Expiry Events, Max Idle Accesses)

import { argon2id } from 'hash-wasm';
//import CryptoJS from 'crypto-js';
const VaultLib = (() => {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  // --- Helper Functions ---
  function toBase64(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
  }

  function fromBase64(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  }

  function combineScope(parts) {
    if (!Array.isArray(parts) || parts.length === 0) {
      throw new Error('Scope parts must be a non-empty array of strings.');
    }
    if (!parts.every(p => typeof p === 'string')) {
      throw new Error('All scope parts must be strings.');
    }
    return encoder.encode(parts.join(''));
  }

  async function derivePasswordKey(password, salt) {
    const hash = await argon2id({
      password,
      salt: new Uint8Array(salt),
      parallelism: 1,
      iterations: 3,
      memorySize: 65536,
      hashLength: 32,
      outputType: 'binary'
    });
    return crypto.subtle.importKey('raw', hash, 'AES-GCM', false, ['encrypt', 'decrypt']);
  }

  async function furtherEncryptPasswordKey(passwordKeyRaw, scopeParts) {
    const combined = combineScope(scopeParts);
    const combinedHash = await crypto.subtle.digest('SHA-256', combined);
    const encryptionKey = await crypto.subtle.importKey('raw', combinedHash, 'AES-GCM', false, ['encrypt', 'decrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedKey = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, encryptionKey, passwordKeyRaw);
    return {
      encryptedPasswordKey: toBase64(encryptedKey),
      iv: toBase64(iv)
    };
  }





  async function furtherDecryptPasswordKey(encryptedPasswordKeyBase64, scopeParts, ivBase64) {
    const combined = combineScope(scopeParts);
    const combinedHash = await crypto.subtle.digest('SHA-256', combined);
    const encryptionKey = await crypto.subtle.importKey('raw', combinedHash, 'AES-GCM', false, ['encrypt', 'decrypt']);
    const iv = new Uint8Array(fromBase64(ivBase64));
    const encryptedPasswordKey = fromBase64(encryptedPasswordKeyBase64);
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, encryptionKey, encryptedPasswordKey));
  }





  async function deriveHmacKey(password, salt) {
    const hash = await argon2id({
      password,
      salt: new Uint8Array(salt),
      parallelism: 1,
      iterations: 3,
      memorySize: 65536,
      hashLength: 32,
      outputType: 'binary'
    });
    return hash;
  }

  async function aesEncrypt(data, key, iv) {
    return crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoder.encode(data));
  }

  async function aesDecrypt(ciphertext, key, iv) {
    return decoder.decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext));
  }

  async function generateVaultKey() {
    return crypto.getRandomValues(new Uint8Array(32));
  }

  async function encryptVaultKey(vaultKey, passwordKey) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedVaultKey = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, passwordKey, vaultKey);
    return { encryptedVaultKey: toBase64(encryptedVaultKey), iv: toBase64(iv) };
  }

  async function decryptVaultKey(encryptedVaultKey, passwordKey, ivBase64) {
    const iv = new Uint8Array(fromBase64(ivBase64));
    const ciphertext = fromBase64(encryptedVaultKey);
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, passwordKey, ciphertext));
  }

  async function encryptVaultData(vaultKey, dataObject) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await crypto.subtle.importKey('raw', vaultKey, 'AES-GCM', false, ['encrypt', 'decrypt']);
    const ciphertext = await aesEncrypt(JSON.stringify(dataObject), key, iv);
    return { encryptedData: toBase64(ciphertext), iv: toBase64(iv) };
  }

  async function decryptVaultData(vaultKey, encryptedData, ivBase64) {
    const iv = new Uint8Array(fromBase64(ivBase64));
    const ciphertext = fromBase64(encryptedData);
    const key = await crypto.subtle.importKey('raw', vaultKey, 'AES-GCM', false, ['decrypt']);
    const decrypted = await aesDecrypt(ciphertext, key, iv);
    return JSON.parse(decrypted);
  }

  async function changeMasterPassword(vaultKey, newPassword, scopeParts) {
    const salt = crypto.getRandomValues(new Uint8Array(16)); 
    const passwordKeyRaw = await argon2id({
      password: newPassword,
      salt: salt,
      parallelism: 1,
      iterations: 3,
      memorySize: 65536,
      hashLength: 32,
      outputType: 'binary'
    });
    const combined = combineScope(scopeParts);
    const combinedHash = await crypto.subtle.digest('SHA-256', combined);
    const encryptionKey = await crypto.subtle.importKey('raw', combinedHash, 'AES-GCM', false, ['encrypt', 'decrypt']);
    const derivedPasswordKey = await crypto.subtle.importKey('raw', passwordKeyRaw, 'AES-GCM', false, ['encrypt', 'decrypt']);
    const { encryptedVaultKey, iv } = await encryptVaultKey(vaultKey, derivedPasswordKey);
    return { encryptedVaultKey, salt: toBase64(salt), iv };
  }


async function createBackup(vaultDataArray, backupPassword) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  const passwordKey = await derivePasswordKey(backupPassword, salt);
  const encryptedVault = await aesEncrypt(JSON.stringify(vaultDataArray), passwordKey, iv);

  const hmacKey = await deriveHmacKey(backupPassword, salt);
  const signature = await crypto.subtle.sign(
    "HMAC",
    hmacKey,
    encryptedVault
  );

  return {
    version: 1,
    encryptedVault: toBase64(encryptedVault),
    salt: toBase64(salt),
    iv: toBase64(iv),
    hmac: toBase64(signature),
    createdAt: new Date().toISOString(),
  };
}

async function restoreBackup(backupFileContent, backupPassword) {
  const backup = JSON.parse(backupFileContent);
  const salt = fromBase64(backup.salt);
  const iv = fromBase64(backup.iv);
  const ciphertext = fromBase64(backup.encryptedVault);

  const hmacKey = await deriveHmacKey(backupPassword, salt);
  const expectedSignature = fromBase64(backup.hmac);

  const valid = await crypto.subtle.verify(
    "HMAC",
    hmacKey,
    expectedSignature,
    ciphertext
  );

  if (!valid) {
    throw new Error("Backup integrity check failed!");
  }

  const passwordKey = await derivePasswordKey(backupPassword, salt);
  const decrypted = await aesDecrypt(ciphertext, passwordKey, iv);
  return JSON.parse(decrypted);
}


  async function getMasterPassword(password, scopeParts, salt) {
    const passwordKeyRaw = await argon2id({
      password,
      salt: new Uint8Array(salt),
      parallelism: 1,
      iterations: 3,
      memorySize: 65536,
      hashLength: 32,
      outputType: 'binary'
    });
    const combined = combineScope(scopeParts);
    const combinedHash = await crypto.subtle.digest('SHA-256', combined);
    const encryptionKey = await crypto.subtle.importKey('raw', combinedHash, 'AES-GCM', false, ['encrypt', 'decrypt']);
    const derivedPasswordKey = await crypto.subtle.importKey('raw', passwordKeyRaw, 'AES-GCM', false, ['encrypt', 'decrypt']);
    
   
    return derivedPasswordKey;
  }

  async function verifyMasterPassword(password, scopeParts, salt, encryptedVaultKeyBase64, vaultKeyIvBase64) {
    try {
      const passwordKey = await getMasterPassword(password, scopeParts, salt);
      await decryptVaultKey(encryptedVaultKeyBase64, passwordKey, vaultKeyIvBase64);
      return true;
    } catch (e) {
      //console.log(e)
      return false;
    }
  }

  const vaultSession = {
    vaults: {},
    defaultTimeoutMinutes: 10,
    defaultMaxIdleAccesses: 50,

    clear() {
      for (const id in this.vaults) {
        if (this.vaults[id].timeoutId) clearTimeout(this.vaults[id].timeoutId);
      }
      this.vaults = {};
    },

    setVault(id, passwordKey, vaultKey, timeoutMinutes, onExpire, maxIdleAccesses) {
      if (this.vaults[id] && this.vaults[id].timeoutId) clearTimeout(this.vaults[id].timeoutId);
      const timeout = timeoutMinutes ?? this.defaultTimeoutMinutes;
      const timeoutId = setTimeout(() => {
        if (this.vaults[id]?.onExpire) this.vaults[id].onExpire(id);
        delete this.vaults[id];
      }, timeout * 60 * 1000);
      this.vaults[id] = {
        passwordKey,
        vaultKey,
        timeoutMinutes: timeout,
        timeoutId,
        onExpire,
        accessCount: 0,
        maxIdleAccesses: maxIdleAccesses ?? this.defaultMaxIdleAccesses
      };
    },

    getVault(id) {
      const vault = this.vaults[id];
      if (vault) {
        if (vault.timeoutId) clearTimeout(vault.timeoutId);
        vault.accessCount++;
        if (vault.accessCount > (vault.maxIdleAccesses ?? this.defaultMaxIdleAccesses)) {
          if (vault.onExpire) vault.onExpire(id);
          delete this.vaults[id];
          return null;
        }
        const timeoutId = setTimeout(() => {
          if (vault.onExpire) vault.onExpire(id);
          delete this.vaults[id];
        }, (vault.timeoutMinutes ?? this.defaultTimeoutMinutes) * 60 * 1000);
        vault.timeoutId = timeoutId;
      }
      return vault || null;
    },

    removeVault(id) {
      if (this.vaults[id]) {
        if (this.vaults[id].timeoutId) clearTimeout(this.vaults[id].timeoutId);
        delete this.vaults[id];
      }
    }
  };

  return {
    toBase64,
    fromBase64,

    derivePasswordKey,
    generateVaultKey,

    encryptVaultKey,
    decryptVaultKey,

    encryptVaultData,
    decryptVaultData,

    changeMasterPassword,
    createBackup,
    restoreBackup,

    furtherEncryptPasswordKey,
    furtherDecryptPasswordKey,

    getMasterPassword,
    verifyMasterPassword,

    vaultSession
  };
})();

export default VaultLib;

//// Somewhere in your frontend code
//import { VaultLib } from './vault-lib'; // or wherever you export it

// async function userLoginFlow(userInputPassword, vaultMetadata) {
//   // vaultMetadata comes from your database/server
//   // Example:
//   // {
//   //   vaultId: "user-123-vault",
//   //   encryptedVaultKey: "base64...",
//   //   vaultKeyIv: "base64...",
//   //   salt: "base64...",
//   //   scopeParts: ["user-123", "app-prod"]
//   // }

//   try {
//     const passwordKey = await VaultLib.getMasterPassword(
//       userInputPassword,
//       vaultMetadata.scopeParts,
//       new Uint8Array(VaultLib.fromBase64(vaultMetadata.salt))
//     );

//     const vaultKey = await VaultLib.decryptVaultKey(
//       vaultMetadata.encryptedVaultKey,
//       passwordKey,
//       vaultMetadata.vaultKeyIv
//     );

//     VaultLib.vaultSession.setVault(
//       vaultMetadata.vaultId,
//       passwordKey,
//       vaultKey,
//       10, // timeout in minutes
//       (id) => console.log(`Vault ${id} expired.`),
//       50 // max idle accesses
//     );

//     console.log("Vault unlocked and stored in session!");
//   } catch (error) {
//     console.error("Invalid password or corrupted vault data!", error);
//   }
// }



// const vaultEntry = VaultLib.vaultSession.getVault(vaultId);
// if (!vaultEntry) {
//   console.error('Vault session expired or not found.');
//   return;
// }
// const { vaultKey } = vaultEntry;


// async function encryptUserData(vaultKey, userDataObject) {
//     const result = await VaultLib.encryptVaultData(vaultKey, userDataObject);
//     return {
//       encryptedData: result.encryptedData,
//       iv: result.iv
//     };
//   }
  
//   // Example:
//   const sensitiveInfo = { cardNumber: '4111 1111 1111 1111', expiry: '12/26' };
//   const encrypted = await encryptUserData(vaultKey, sensitiveInfo);
//   console.log('Encrypted:', encrypted.encryptedData);
//   console.log('IV:', encrypted.iv);



// async function decryptUserData(vaultKey, encryptedData, iv) {
//     const decryptedObject = await VaultLib.decryptVaultData(vaultKey, encryptedData, iv);
//     return decryptedObject;
//   }
  
//   // Example:
//   const decrypted = await decryptUserData(vaultKey, encrypted.encryptedData, encrypted.iv);
//   console.log('Decrypted:', decrypted);
  

